import React, { useState, useEffect } from 'react';

// Enhanced PublishKey component to handle the automatic generation and user-triggered generation
const PublishKey = ({ token, userid, onKeyGenerated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState('');
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  // Function to generate publish key
  const handlePublishKey = async (showFeedback = true) => {
    setIsLoading(true);
    setCopied(false);
    
    try {
      // Make API call
      const response = await fetch('https://api.dqr-display-tms.bonrix.in/api/DeviceJsonSettingOperation/SubUser/PublishId', {
        method: 'GET',
        headers: {
          'accept': 'text/plain',
          'UserId': userid,
          'X-Authorization': token,
        }
      });
      
      // Parse the JSON response
      const data = await response.json();
      setResponse(data);
      
      // Callback for parent component
      if (onKeyGenerated && data && data.value) {
        onKeyGenerated(data.value);
      }
      
      // Automatically copy the value to clipboard
      if (data && data.value) {
        await navigator.clipboard.writeText(data.value);
        setCopied(true);
        
        if (showFeedback) {
          setMessage("Key published and copied to clipboard!");
          setShowCopiedTooltip(true);
          setTimeout(() => setShowCopiedTooltip(false), 3000);
        }
      }
    } catch (error) {
      console.error('Error publishing key:', error);
      setResponse({ 
        isresponse: false, 
        responsestatus: "ERROR", 
        errormsg: "Failed to publish key. Please try again." 
      });
      
      if (showFeedback) {
        setMessage("Failed to publish key. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Generate key on component mount
  useEffect(() => {
    handlePublishKey(false); // Don't show feedback on initial load
  }, []);
  
  // Handle manual key publish
  const handleManualPublish = () => {
    handlePublishKey(true); // Show feedback on manual publish
  };
  
  // Copy key to clipboard
  const copyKeyToClipboard = async () => {
    if (response && response.value) {
      try {
        await navigator.clipboard.writeText(response.value);
        setCopied(true);
        setMessage("Key copied to clipboard!");
        setShowCopiedTooltip(true);
        setTimeout(() => setShowCopiedTooltip(false), 3000);
      } catch (error) {
        console.error('Failed to copy key:', error);
        setMessage("Failed to copy key to clipboard");
      }
    }
  };

  return (
    <div className="publish-key-container">
      <button 
        type="button" 
        className="btn btn-primary" 
        onClick={handleManualPublish}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Publishing...
          </>
        ) : (
          <>
            <i className="fas fa-key mr-2"></i>
            Publish Key
          </>
        )}
      </button>
      
      {response && response.value && (
        <div className="key-display mt-2">
          <div className="input-group">
            <input 
              type="text" 
              className="form-control" 
              value={response.value} 
              readOnly 
            />
            <div className="input-group-append">
              <button 
                className="btn btn-outline-secondary" 
                type="button" 
                onClick={copyKeyToClipboard}
                title="Copy to clipboard"
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>
          
          {showCopiedTooltip && (
            <div className="copied-tooltip">
              <div className="alert alert-success py-1 px-2 mt-1 mb-0 small">
                {message}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublishKey;